<script setup lang="ts">
import { ref } from 'vue'
import { getCsrfToken } from '../api'

const props = defineProps<{ speakerId: string }>()
const emit = defineEmits<{ uploaded: [] }>()

interface UploadItem {
  id: string
  name: string
  progress: number
  state: 'uploading' | 'done' | 'error'
  error: string
}

const input = ref<HTMLInputElement | null>(null)
const dragging = ref(false)
const uploads = ref<UploadItem[]>([])
const allowed = new Set(['mp3', 'm4a', 'wav', 'ogg', 'opus', 'aac', 'mp4', 'webm'])
const maxBytes = 2 * 1024 * 1024 * 1024

function addError(file: File, message: string): void {
  uploads.value.unshift({ id: crypto.randomUUID(), name: file.name, progress: 0, state: 'error', error: message })
}

function upload(file: File): Promise<void> {
  const item: UploadItem = { id: crypto.randomUUID(), name: file.name, progress: 0, state: 'uploading', error: '' }
  uploads.value.unshift(item)
  return new Promise(resolve => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `/api/admin/speakers/${props.speakerId}/recordings`)
    xhr.setRequestHeader('X-CSRF-Token', getCsrfToken())
    xhr.upload.addEventListener('progress', event => {
      if (event.lengthComputable) item.progress = Math.min(99, Math.round(event.loaded / event.total * 100))
    })
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        item.progress = 100
        item.state = 'done'
        emit('uploaded')
      } else {
        item.state = 'error'
        try { item.error = (JSON.parse(xhr.responseText) as { error?: string }).error || 'Не удалось загрузить файл.' }
        catch { item.error = 'Не удалось загрузить файл.' }
      }
      resolve()
    })
    xhr.addEventListener('error', () => {
      item.state = 'error'
      item.error = 'Соединение прервалось. Попробуйте ещё раз.'
      resolve()
    })
    const form = new FormData()
    form.append('file', file)
    xhr.send(form)
  })
}

function choose(files: FileList | File[]): void {
  const selected = Array.from(files)
  for (const file of selected) {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
    if (!allowed.has(extension)) addError(file, 'Этот формат не поддерживается.')
    else if (file.size > maxBytes) addError(file, 'Файл больше 2 ГБ.')
    else void upload(file)
  }
  if (input.value) input.value.value = ''
}

function onDrop(event: DragEvent): void {
  dragging.value = false
  if (event.dataTransfer?.files.length) choose(event.dataTransfer.files)
}
</script>

<template>
  <section class="upload-section">
    <h2>Добавить записи</h2>
    <button
      class="drop-zone"
      :class="{ 'drop-zone--active': dragging }"
      type="button"
      @click="input?.click()"
      @dragenter.prevent="dragging = true"
      @dragover.prevent="dragging = true"
      @dragleave.prevent="dragging = false"
      @drop.prevent="onDrop"
    >
      <strong>Выберите файлы</strong>
      <span>или перетащите сюда</span>
      <small>MP3, M4A, WAV, OGG, OPUS, AAC, MP4, WEBM · до 2 ГБ каждый</small>
    </button>
    <input
      ref="input"
      class="visually-hidden"
      type="file"
      multiple
      accept=".mp3,.m4a,.wav,.ogg,.opus,.aac,.mp4,.webm,audio/*,video/mp4,video/webm"
      @change="choose(($event.target as HTMLInputElement).files ?? [])"
    />

    <div v-if="uploads.length" class="upload-list" aria-live="polite">
      <div v-for="item in uploads" :key="item.id" class="upload-item">
        <div class="upload-item__head">
          <span>{{ item.name }}</span>
          <span v-if="item.state === 'uploading'">{{ item.progress }}%</span>
          <span v-else-if="item.state === 'done'" class="text-success">Загружено</span>
          <span v-else class="text-error">Ошибка</span>
        </div>
        <div v-if="item.state !== 'error'" class="progress"><span :style="{ width: `${item.progress}%` }" /></div>
        <p v-else class="field-error">{{ item.error }}</p>
      </div>
    </div>
  </section>
</template>
